import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';

// ?pit=1 draws the masks as solid magenta discs instead of cutting with them.
//
// "It isn't working" has three possible meanings — no pit was saved, the pit is
// somewhere else, or the stencil is not cutting — and they are indistinguishable
// by looking at unbroken tarmac. Making the mask VISIBLE separates them in one
// glance: no disc means no data, a disc in the wrong place means placement, a
// disc exactly where you want the hole means the cut is the problem.
const DEBUG = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pit') === '1';

// GroundPits — real holes in ground we do not own.
//
// The outer disc (CityBox) is ours and can simply be built with the hole
// subtracted. The ground under the station is not: it is the CITY MODEL's, baked
// into a downloaded GLB as ~1800-unit `RoadsGround` tiles laid over a base block.
// Deleting a tile only exposes the block beneath it, and editing the GLB would
// bake one station's position into the city's geometry forever.
//
// So the hole is cut in the STENCIL BUFFER instead, which does not care what the
// ground is made of, how many layers it has, or who authored it:
//
//   1. A mask disc renders first, writes nothing but stencil, and marks the
//      pixels the pit covers.
//   2. The ground's own materials are switched to "draw only where that mark is
//      absent", so they skip those pixels entirely.
//
// What is behind the ground shows through — which, since the station's stairwell
// is sitting right there, is the stairwell. The station sinks into the city
// without the city being modified.
//
// Reversible by construction: nothing is deleted and no geometry is rewritten,
// so moving a pit moves the hole and clearing one closes it.
const GROUND_RE = /RoadsGround/i;

export default function GroundPits() {
  const scene = useThree((s) => s.scene);
  const pits = useWorldStore((s) => s.groundPits);

  // The materials the ground is drawn with. Collected from the city model rather
  // than named, because a downloaded model's material names are its own business
  // — what identifies ground here is being on a mesh called *RoadsGround*.
  //
  // RETRIED, because the city GLB streams in. Collecting once on mount finds an
  // empty scene and never looks again, which fails exactly like a wrong material
  // name and is the sort of thing that costs an hour to tell apart.
  const [mats, setMats] = useState([]);
  useEffect(() => {
    let tries = 0; let timer = null;
    const scan = () => {
      const found = new Set();
      let meshes = 0;
      scene.traverse((o) => {
        if (!o.isMesh || !GROUND_RE.test(o.name || '')) return;
        meshes++;
        const list = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of list) if (m) found.add(m);
      });
      if (found.size) {
        console.log(`[ground] found ${meshes} ground mesh(es) on ${found.size} material(s)`);
        setMats([...found]);
        return;
      }
      if (++tries < 20) { timer = setTimeout(scan, 500); return; }
      console.warn('[ground] no mesh matching /RoadsGround/i after 10s — the city model either has not loaded or names its ground something else. Run: node scripts/mesh_bounds.mjs public/models/ccity_building_set_1_desktop.glb');
    };
    scan();
    return () => clearTimeout(timer);
  }, [scene]);

  useEffect(() => {
    if (!mats.length) return undefined;
    const on = pits.length > 0;
    for (const m of mats) {
      m.stencilWrite = on;
      m.stencilRef = 1;
      // Draw everywhere the mask did NOT mark. With no pits this is switched
      // off entirely, so the ground behaves exactly as it did before.
      m.stencilFunc = THREE.NotEqualStencilFunc;
      m.needsUpdate = true;
    }
    if (on) {
      console.log(`[ground] stencil pit(s) active on ${mats.length} ground material(s) — if a wall or roof also vanished, the model shares a material between its ground and that surface and the match needs narrowing`);
    }
    return () => {
      for (const m of mats) { m.stencilWrite = false; m.needsUpdate = true; }
    };
  }, [mats, pits]);

  if (!pits.length) return null;

  return (
    <group>
      {pits.map((p, i) => (
        // The mask. Flat on the ground, slightly above it so it cannot z-fight
        // its way in and out of existence, and rendered FIRST — a mask drawn
        // after the thing it masks marks pixels that have already been painted.
        //
        // Turned by the portal's own rotY in an OUTER group rather than folded
        // into the mesh's Euler: the mesh is already rotated flat about X, and
        // combining the two in one triple depends on Euler order in a way that
        // is easy to get subtly wrong and hard to see.
        <group key={`${p.x}:${p.z}:${i}`} position={[p.x, 0.06, p.z]} rotation={[0, p.rotY || 0, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={-1000}>
          <planeGeometry args={[p.w, p.d]} />
          <meshBasicMaterial
            color="#ff00ff"
            colorWrite={DEBUG}
            depthTest={!DEBUG}
            depthWrite={false}
            toneMapped={false}
            stencilWrite
            stencilRef={1}
            stencilFunc={THREE.AlwaysStencilFunc}
            stencilZPass={THREE.ReplaceStencilOp}
          />
        </mesh>
        </group>
      ))}
    </group>
  );
}
